import { normalizeGeneratedText } from "@/lib/normalize-generated-text";
import { coerceQuizOptionLabel } from "@/lib/quiz-option-label";
import type { ModuleBankQuestion } from "@/lib/module-quiz/types";
import type { ServedQuestionRef } from "@/lib/module-quiz/types";

export type ModuleQuizClientQuestion = {
  questionId: number;
  question_text: string;
  question_type: string;
  options: unknown[];
  order_index: number;
};

export type ModuleQuizClientPayload = {
  title: string;
  description: string | null;
  questions: ModuleQuizClientQuestion[];
};

export function buildModuleQuizClientPayload(
  moduleTitle: string,
  served: ServedQuestionRef[],
  bankById: Map<number, ModuleBankQuestion>,
): ModuleQuizClientPayload {
  const questions: ModuleQuizClientQuestion[] = [];

  for (const ref of [...served].sort(
    (a, b) => a.displayOrder - b.displayOrder,
  )) {
    const row = bankById.get(ref.quizQuestionId);
    if (!row) continue;
    questions.push({
      questionId: ref.quizQuestionId,
      question_text: row.question_text,
      question_type: row.question_type,
      options: Array.isArray(row.options) ? row.options : [],
      order_index: ref.displayOrder,
    });
  }

  return {
    title: `${moduleTitle} — Module Quiz`,
    description: null,
    questions,
  };
}

export type ModuleQuizClientDisplayQuestion = {
  id: number;
  text: string;
  options: string[];
  /** Only populated in review mode (already-passed attempts) — a live
   * in-progress quiz never receives the correct answer from the backend. */
  correctOptionIndex?: number;
  explanation?: string | null;
};

export function mapModuleQuizClientQuestions(
  rawQuestions: unknown[],
):
  | { ok: true; questions: ModuleQuizClientDisplayQuestion[] }
  | { ok: false; error: string } {
  if (!Array.isArray(rawQuestions) || rawQuestions.length === 0) {
    return { ok: false, error: "No module quiz questions available." };
  }

  const questions: ModuleQuizClientDisplayQuestion[] = [];

  for (let index = 0; index < rawQuestions.length; index++) {
    const raw = rawQuestions[index];
    if (!raw || typeof raw !== "object") {
      return { ok: false, error: `Invalid question at ${index + 1}` };
    }
    const q = raw as Record<string, unknown>;
    const rawOptions = Array.isArray(q.options) ? q.options : [];
    const optionLabels = rawOptions
      .map((opt) => coerceQuizOptionLabel(opt).trim())
      .filter((label) => label.length > 0);
    if (optionLabels.length === 0) {
      return { ok: false, error: `Missing choices for question ${index + 1}` };
    }
    const id =
      typeof q.questionId === "number"
        ? q.questionId
        : typeof q.question_id === "number"
          ? q.question_id
          : index + 1;
    const text = normalizeGeneratedText(
      String(q.question_text ?? q.text ?? `Question ${index + 1}`),
    );

    const correctAnswerRaw = q.correct_answer ?? q.correctAnswer;
    let correctOptionIndex: number | undefined;
    if (typeof correctAnswerRaw === "string" && correctAnswerRaw.trim()) {
      const normalizedCorrect = correctAnswerRaw.trim().toLowerCase();
      const letterMap: Record<string, number> = { a: 0, b: 1, c: 2, d: 3 };
      let matchIdx = optionLabels.findIndex(
        (label) => label.trim().toLowerCase() === normalizedCorrect,
      );
      if (matchIdx === -1 && normalizedCorrect in letterMap) {
        matchIdx = letterMap[normalizedCorrect];
      }
      if (matchIdx >= 0) correctOptionIndex = matchIdx;
    }

    const explanation =
      typeof q.explanation === "string" ? q.explanation : null;

    questions.push({ id, text, options: optionLabels, correctOptionIndex, explanation });
  }

  return { ok: true, questions };
}

export type ModuleQuizStartPayload = {
  attemptUuid?: string;
  attempt_uuid?: string;
  title?: string;
  questions: unknown[];
  resumed?: boolean;
  savedAnswers?: Record<string, number>;
  saved_answers?: Record<string, number>;
  currentQuestion?: number;
  current_question?: number;
  reviewMode?: boolean;
  passed?: boolean;
  score?: number;
  previousAttemptScore?: number | null;
  previousAttemptWrongCount?: number | null;
  previousAttemptPassed?: boolean | null;
  passThreshold?: number | null;
};

export type ModuleQuizInitResult =
  | {
      ok: true;
      attemptUuid: string;
      title: string;
      questions: ModuleQuizClientDisplayQuestion[];
      resumed: boolean;
      savedAnswers: Record<number, number>;
      currentQuestion: number;
      /** Already-passed attempt reopened for read-only review — see
       * `start_module_quiz`'s review-mode branch in `backend/app/api/v1/endpoints/modules.py`. */
      reviewMode: boolean;
      passed: boolean;
      score: number | null;
      previousAttemptScore?: number | null;
      previousAttemptWrongCount?: number | null;
      previousAttemptPassed?: boolean | null;
      passThreshold?: number | null;
    }
  | { ok: false; error: string };

export function parseModuleQuizStartResponse(
  data: ModuleQuizStartPayload,
): ModuleQuizInitResult {
  const mapped = mapModuleQuizClientQuestions(data.questions);
  if (!mapped.ok) {
    return mapped;
  }
  const attemptUuid = data.attemptUuid || data.attempt_uuid || "";
  const rawAnswers = data.savedAnswers || data.saved_answers || {};
  const savedAnswers: Record<number, number> = {};
  for (const [k, v] of Object.entries(rawAnswers)) {
    const idx = parseInt(k.replace("questionIndex_", ""), 10);
    if (!isNaN(idx) && typeof v === "number") {
      savedAnswers[idx] = v;
    }
  }
  const currentQuestion = data.currentQuestion ?? data.current_question ?? 0;

  return {
    ok: true,
    attemptUuid,
    title: normalizeGeneratedText(data.title ?? "Module Quiz"),
    questions: mapped.questions,
    resumed: Boolean(data.resumed),
    savedAnswers,
    currentQuestion: Math.min(Math.max(0, currentQuestion), mapped.questions.length - 1),
    reviewMode: Boolean(data.reviewMode),
    passed: Boolean(data.passed),
    score: typeof data.score === "number" ? data.score : null,
    previousAttemptScore: typeof data.previousAttemptScore === "number" ? data.previousAttemptScore : null,
    previousAttemptWrongCount: typeof data.previousAttemptWrongCount === "number" ? data.previousAttemptWrongCount : null,
    previousAttemptPassed: typeof data.previousAttemptPassed === "boolean" ? data.previousAttemptPassed : null,
    passThreshold: typeof data.passThreshold === "number" ? data.passThreshold : 80,
  };
}
